import { useState } from 'react';
import { Alert, Button, Input } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { submitIdentifier } from '@/api/dataroom';
import { normalizeError } from '@/api/errors';
import type { IdentifierSpec, Task, TaskStatus } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { useTaskText } from './taskText';
import { checkIdentifier, normalizeIdentifier } from '@/validation/formats';

/**
 * IDENTIFIER task (OL-22 slice 2): one official number (PAN, GSTIN, CIN, Udyam, TAN, IEC, voter
 * ID, driving licence, passport, wallet). The room checks shape and checksum as the applicant
 * types; the registry lookup runs later, in the flow — the applicant never sees a lookup result.
 */
export function IdentifierTask({
  task,
  onStatus,
}: {
  task: Task;
  onStatus: (status: TaskStatus) => void;
}) {
  const { t } = useI18n();
  const text = useTaskText(task);
  const scheme = (task.spec as IdentifierSpec | undefined)?.scheme ?? 'PAN';
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (task.status !== 'PENDING' || done) {
    return <Alert type="success" showIcon message={t('identifier.submitted')} />;
  }

  const normalized = normalizeIdentifier(scheme, value);
  const problem = checkIdentifier(scheme, normalized);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await submitIdentifier(task.taskId, normalized);
      onStatus(res.status);
      setDone(true);
    } catch (err) {
      setError(
        normalizeError(err).kind === 'INVALID'
          ? t('identifier.invalid', { scheme: t(`identifier.scheme.${scheme}`) })
          : t('error.inline'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dr-stack">
      <label className="dr-field">
        <span>{text('label', `identifier.scheme.${scheme}`)}</span>
        <Input
          size="large"
          value={value}
          spellCheck={false}
          autoCapitalize={scheme === 'WALLET' ? 'off' : 'characters'}
          placeholder={text('placeholder', `identifier.example.${scheme}`)}
          status={touched && problem && problem !== 'REQUIRED' ? 'error' : undefined}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => setTouched(true)}
          data-testid="identifier-input"
        />
      </label>
      {touched && problem && problem !== 'REQUIRED' && (
        <div className="dr-meta dr-meta--error" role="alert">
          {t(problem === 'CHECKSUM' ? 'identifier.checksum' : 'identifier.format', {
            scheme: t(`identifier.scheme.${scheme}`),
          })}
        </div>
      )}
      {error && <Alert type="error" showIcon message={error} />}
      <Button
        type="primary"
        size="large"
        block
        disabled={problem !== null}
        loading={busy}
        icon={<SendOutlined />}
        onClick={submit}
      >
        {text('submit', 'identifier.submit')}
      </Button>
    </div>
  );
}
